import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import {
    Box, Button, Card, Chip, CircularProgress,
    Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, Stack, Table, TableBody, TableCell,
    TableContainer, TableRow, TextField, Typography,
    Paper, Tooltip, Avatar, Checkbox, FormControlLabel, FormGroup,
    Divider, MenuItem, Select, InputLabel, FormControl, Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { TokenContext } from '../Context/TokenContext';
import { UserContext } from '../Context/UserContext';
import { PERMISSION_SECTIONS } from '../constants/permissions';

const API_BASE = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8080';

function initials(nom, prenom) {
    return ((prenom?.[0] || '') + (nom?.[0] || '')).toUpperCase();
}

// Construit un arbre indenté à partir de la liste plate (un parent peut avoir plusieurs
// enfants) : les racines d'abord, puis chaque niveau d'enfants juste après son parent.
function buildTree(groups) {
    const byParent = {};
    for (const g of groups) {
        const key = g.id_groupe_parent ?? 'root';
        (byParent[key] || (byParent[key] = [])).push(g);
    }
    const ordered = [];
    const walk = (parentKey, depth) => {
        for (const g of (byParent[parentKey] || [])) {
            ordered.push({ ...g, depth });
            walk(g.id_groupe, depth);
        }
    };
    walk('root', 0);
    return ordered;
}

export default function GroupesPage() {
    const { accessToken } = useContext(TokenContext);
    const { userInfo } = useContext(UserContext);
    const isSuperAdmin = userInfo?.is_super_admin === true;

    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [myGroupIds, setMyGroupIds] = useState(null); // null = pas encore calculé (super-admin: inutile)

    const [selectedId, setSelectedId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState('');

    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState({ nom: '', id_groupe_parent: '' });
    const [createSaving, setCreateSaving] = useState(false);
    const [createError, setCreateError] = useState('');

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    const [nomDraft, setNomDraft] = useState('');
    const [parentDraft, setParentDraft] = useState('');
    const [permCurrent, setPermCurrent] = useState([]);
    const [savingGroup, setSavingGroup] = useState(false);
    const [saveError, setSaveError] = useState('');

    const [memberPicker, setMemberPicker] = useState(null);
    const [candidateEmployes, setCandidateEmployes] = useState([]);
    const [addingMember, setAddingMember] = useState(false);
    const [membersError, setMembersError] = useState('');

    const headers = { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' };

    const loadGroups = useCallback(async () => {
        setLoading(true);
        setLoadError('');
        try {
            const r = await fetch(`${API_BASE}/api/groupes`, { headers });
            const data = await r.json().catch(() => null);
            if (r.ok) {
                setGroups(data || []);
            } else {
                setLoadError(data?.error || `Erreur lors du chargement des groupes (${r.status}).`);
            }
        } catch (e) {
            setLoadError('Erreur réseau lors du chargement des groupes.');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken]);

    useEffect(() => { loadGroups(); }, [loadGroups]);

    // Pour un non-super-admin (porteur scopé de manage-own-group-members), l'arbre est filtré
    // aux groupes dont il est lui-même membre — déterminé en chargeant le détail de chaque
    // groupe (dataset attendu petit, table de config) puisque la liste ne renvoie qu'un compte
    // de membres, pas leur identité.
    useEffect(() => {
        if (isSuperAdmin || groups.length === 0) { setMyGroupIds(isSuperAdmin ? null : new Set()); return; }
        let cancelled = false;
        (async () => {
            const mine = new Set();
            await Promise.all(groups.map(async g => {
                try {
                    const r = await fetch(`${API_BASE}/api/groupes/${g.id_groupe}`, { headers });
                    if (!r.ok) return;
                    const d = await r.json();
                    if ((d.membres || []).some(m => m.public_id === userInfo?.user_key)) mine.add(g.id_groupe);
                } catch (e) { /* ignore */ }
            }));
            if (!cancelled) setMyGroupIds(mine);
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groups, isSuperAdmin]);

    const visibleGroups = useMemo(() => {
        if (isSuperAdmin || myGroupIds === null) return groups;
        return groups.filter(g => myGroupIds.has(g.id_groupe));
    }, [groups, myGroupIds, isSuperAdmin]);

    const tree = useMemo(() => buildTree(visibleGroups), [visibleGroups]);

    const loadDetail = useCallback(async (id) => {
        setDetailLoading(true);
        setDetailError('');
        try {
            const r = await fetch(`${API_BASE}/api/groupes/${id}`, { headers });
            const data = await r.json();
            if (!r.ok) { setDetailError(data.error || 'Erreur lors du chargement du groupe.'); return; }
            setDetail(data);
            setNomDraft(data.nom);
            setParentDraft(data.id_groupe_parent ?? '');
            setPermCurrent(data.effective_permissions || []);
        } catch (e) {
            setDetailError('Erreur réseau.');
        } finally {
            setDetailLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken]);

    useEffect(() => {
        if (selectedId != null) loadDetail(selectedId);
    }, [selectedId, loadDetail]);

    // Permissions héritées du parent (déjà calculées côté serveur pour chaque groupe listé) —
    // sert de baseline pour l'éditeur de permissions du groupe sélectionné.
    const parentBaseline = useMemo(() => {
        if (!detail || detail.id_groupe_parent == null) return [];
        const parent = groups.find(g => g.id_groupe === detail.id_groupe_parent);
        return parent?.effective_permissions || [];
    }, [detail, groups]);

    const togglePermission = (key) => {
        setPermCurrent(cur => cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key]);
    };

    const canManageThisGroup = isSuperAdmin || (myGroupIds && selectedId != null && myGroupIds.has(selectedId));

    // ── Créer un groupe ──────────────────────────────────────────────────────────
    const openCreate = () => {
        setCreateForm({ nom: '', id_groupe_parent: selectedId ?? '' });
        setCreateError('');
        setCreateOpen(true);
    };

    const handleCreate = async () => {
        if (!createForm.nom.trim()) { setCreateError('Le nom est requis.'); return; }
        setCreateSaving(true);
        setCreateError('');
        try {
            const r = await fetch(`${API_BASE}/api/groupes`, {
                method: 'POST', headers,
                body: JSON.stringify({ nom: createForm.nom, id_groupe_parent: createForm.id_groupe_parent || null, permissions: [] })
            });
            const data = await r.json();
            if (!r.ok) { setCreateError(data.error || 'Erreur lors de la création.'); return; }
            setCreateOpen(false);
            await loadGroups();
            setSelectedId(data.id_groupe);
        } catch (e) {
            setCreateError('Erreur réseau.');
        } finally {
            setCreateSaving(false);
        }
    };

    // ── Enregistrer nom / parent / permissions du groupe sélectionné ─────────────
    const handleSaveGroup = async () => {
        if (!detail) return;
        setSavingGroup(true);
        setSaveError('');
        try {
            const body = { nom: nomDraft, id_groupe_parent: parentDraft === '' ? null : parentDraft };
            if (isSuperAdmin) {
                const baselineSet = new Set(parentBaseline);
                const currentSet = new Set(permCurrent);
                const universe = new Set([...baselineSet, ...currentSet]);
                const diff = [];
                for (const key of universe) {
                    const inBaseline = baselineSet.has(key);
                    const inCurrent = currentSet.has(key);
                    if (inCurrent && !inBaseline) diff.push({ permission_key: key, effect: 'grant' });
                    else if (!inCurrent && inBaseline) diff.push({ permission_key: key, effect: 'revoke' });
                }
                body.permissions = diff;
            }

            const r = await fetch(`${API_BASE}/api/groupes/${detail.id_groupe}`, { method: 'PUT', headers, body: JSON.stringify(body) });
            const data = await r.json();
            if (!r.ok) { setSaveError(data.error || 'Erreur lors de la sauvegarde.'); return; }
            await loadGroups();
            await loadDetail(detail.id_groupe);
        } catch (e) {
            setSaveError('Erreur réseau.');
        } finally {
            setSavingGroup(false);
        }
    };

    // ── Supprimer un groupe ───────────────────────────────────────────────────────
    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        setDeleteError('');
        try {
            const r = await fetch(`${API_BASE}/api/groupes/${deleteTarget.id_groupe}`, { method: 'DELETE', headers });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) { setDeleteError(data.error || 'Erreur lors de la suppression.'); return; }
            setDeleteTarget(null);
            if (selectedId === deleteTarget.id_groupe) { setSelectedId(null); setDetail(null); }
            loadGroups();
        } catch (e) {
            setDeleteError('Erreur réseau.');
        } finally {
            setDeleting(false);
        }
    };

    // ── Membres ────────────────────────────────────────────────────────────────
    const openMemberPicker = async () => {
        setMembersError('');
        setMemberPicker({});
        try {
            const r = await fetch(`${API_BASE}/api/admins`, { headers });
            const data = await r.json();
            if (r.ok) {
                const currentIds = new Set((detail?.membres || []).map(m => m.id_employe));
                setCandidateEmployes((data || []).filter(a => !a.is_super_admin && !currentIds.has(a.id)));
            }
        } catch (e) { /* ignore */ }
    };

    const handleAddMember = async (employe) => {
        if (!employe || !detail) return;
        setAddingMember(true);
        setMembersError('');
        try {
            const r = await fetch(`${API_BASE}/api/groupes/${detail.id_groupe}/membres`, {
                method: 'POST', headers, body: JSON.stringify({ id_employe: employe.id })
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) { setMembersError(data.error || "Erreur lors de l'ajout."); return; }
            setMemberPicker(null);
            await loadDetail(detail.id_groupe);
            loadGroups();
        } catch (e) {
            setMembersError('Erreur réseau.');
        } finally {
            setAddingMember(false);
        }
    };

    const handleRemoveMember = async (id_employe) => {
        if (!detail) return;
        try {
            await fetch(`${API_BASE}/api/groupes/${detail.id_groupe}/membres/${id_employe}`, { method: 'DELETE', headers });
            await loadDetail(detail.id_groupe);
            loadGroups();
        } catch (e) { /* ignore */ }
    };

    const parentOptions = groups.filter(g => !detail || g.id_groupe !== detail.id_groupe);

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1300, mx: 'auto' }}>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
                <GroupIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                <Box>
                    <Typography variant="h5" fontWeight={700}>Gestion des groupes</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {isSuperAdmin
                            ? 'Équipes, hiérarchie et permissions (héritage du parent, surcharges locales)'
                            : "Vos groupes — vous pouvez y ajouter ou retirer des membres"}
                    </Typography>
                </Box>
            </Stack>

            {loadError && <Typography color="error" variant="body2" sx={{ mb: 2 }}>{loadError}</Typography>}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ xs: 'stretch', md: 'flex-start' }}>
                {/* Arbre des groupes */}
                <Card elevation={2} sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
                    <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2">Groupes</Typography>
                        {isSuperAdmin && (
                            <Tooltip title="Nouveau groupe">
                                <IconButton size="small" onClick={openCreate} sx={{ color: 'primary.main' }}>
                                    <AddIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                    <Divider />
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={24} sx={{ color: 'primary.main' }} />
                        </Box>
                    ) : tree.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                            {isSuperAdmin ? "Aucun groupe. Créez-en un." : "Vous n'êtes membre d'aucun groupe."}
                        </Typography>
                    ) : (
                        <Box sx={{ py: 0.5 }}>
                            {tree.map(g => (
                                <Box
                                    key={g.id_groupe}
                                    onClick={() => setSelectedId(g.id_groupe)}
                                    sx={{
                                        pl: 1.5 + g.depth * 2, pr: 1.5, py: 0.75, cursor: 'pointer',
                                        bgcolor: selectedId === g.id_groupe ? 'action.selected' : 'transparent',
                                        '&:hover': { bgcolor: 'action.hover' },
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography variant="body2" noWrap>{g.nom}</Typography>
                                    <Chip label={g.membres_count} size="small" sx={{ ml: 1 }} />
                                </Box>
                            ))}
                        </Box>
                    )}
                </Card>

                {/* Détail du groupe sélectionné */}
                <Card elevation={2} sx={{ flexGrow: 1, minHeight: 300 }}>
                    {selectedId == null ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">Sélectionnez un groupe.</Typography>
                        </Box>
                    ) : detailLoading || !detail ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress sx={{ color: 'primary.main' }} />
                        </Box>
                    ) : (
                        <Box sx={{ p: 3 }}>
                            {detailError && <Typography color="error" variant="body2" sx={{ mb: 2 }}>{detailError}</Typography>}

                            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                                {isSuperAdmin ? (
                                    <TextField label="Nom du groupe" size="small" value={nomDraft} onChange={e => setNomDraft(e.target.value)} sx={{ flexGrow: 1 }} />
                                ) : (
                                    <Typography variant="h6">{detail.nom}</Typography>
                                )}
                                {isSuperAdmin && (
                                    <Tooltip title="Supprimer ce groupe">
                                        <IconButton color="error" onClick={() => setDeleteTarget(detail)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Stack>

                            {isSuperAdmin && (
                                <FormControl size="small" sx={{ minWidth: 240, mb: 2 }}>
                                    <InputLabel>Groupe parent</InputLabel>
                                    <Select value={parentDraft} label="Groupe parent" onChange={e => setParentDraft(e.target.value)}>
                                        <MenuItem value="">— Aucun (racine) —</MenuItem>
                                        {parentOptions.map(g => (
                                            <MenuItem key={g.id_groupe} value={g.id_groupe}>{g.nom}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}

                            {detail.parent_chain?.length > 0 && (
                                <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                                    Hérite de : {detail.parent_chain.map(p => p.nom).join(' > ')}
                                </Typography>
                            )}

                            {isSuperAdmin && (
                                <>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Permissions (héritées du parent affichées cochées ; décochez pour retirer, cochez pour ajouter)
                                    </Typography>
                                    {PERMISSION_SECTIONS.map(sec => {
                                        const sectionOptions = [
                                            ...(sec.pageKey ? [{ key: sec.pageKey, label: `Accès à la page ${sec.label}` }] : []),
                                            ...sec.actions,
                                        ];
                                        if (sectionOptions.length === 0) return null;
                                        return (
                                            <Box key={sec.section} sx={{ mb: 2 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', textTransform: 'uppercase' }}>
                                                    {sec.label}
                                                </Typography>
                                                <FormGroup>
                                                    {sectionOptions.map(({ key, label }) => {
                                                        const inherited = parentBaseline.includes(key);
                                                        const checked = permCurrent.includes(key);
                                                        return (
                                                            <FormControlLabel
                                                                key={key}
                                                                control={<Checkbox checked={checked} onChange={() => togglePermission(key)} />}
                                                                label={
                                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                                        <span>{label}</span>
                                                                        {inherited !== checked && (
                                                                            <Chip label={checked ? 'ajouté ici' : 'retiré ici'} size="small" color={checked ? 'success' : 'error'} variant="outlined" />
                                                                        )}
                                                                        {inherited && checked && <Chip label="hérité" size="small" variant="outlined" />}
                                                                    </Stack>
                                                                }
                                                            />
                                                        );
                                                    })}
                                                </FormGroup>
                                            </Box>
                                        );
                                    })}
                                    {saveError && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{saveError}</Typography>}
                                    <Button
                                        variant="contained" onClick={handleSaveGroup} disabled={savingGroup}
                                        sx={{ mt: 2, bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}
                                    >
                                        {savingGroup ? <CircularProgress size={20} color="inherit" /> : 'Enregistrer'}
                                    </Button>
                                </>
                            )}

                            <Divider sx={{ my: 3 }} />
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="subtitle2" color="text.secondary">Membres ({detail.membres?.length || 0})</Typography>
                                {canManageThisGroup && (
                                    <Button size="small" startIcon={<PersonAddIcon />} onClick={openMemberPicker}>
                                        Ajouter
                                    </Button>
                                )}
                            </Stack>
                            {membersError && <Typography color="error" variant="body2">{membersError}</Typography>}
                            <TableContainer component={Paper} elevation={0} variant="outlined">
                                <Table size="small">
                                    <TableBody>
                                        {(detail.membres || []).length === 0 ? (
                                            <TableRow><TableCell sx={{ color: 'text.secondary' }}>Aucun membre.</TableCell></TableRow>
                                        ) : detail.membres.map(m => (
                                            <TableRow key={m.id_employe} hover>
                                                <TableCell width={44}>
                                                    <Avatar sx={{ bgcolor: 'primary.main', width: 28, height: 28, fontSize: 12 }}>
                                                        {initials(m.nom, m.prenom)}
                                                    </Avatar>
                                                </TableCell>
                                                <TableCell>{m.prenom} {m.nom}</TableCell>
                                                <TableCell>{m.email}</TableCell>
                                                <TableCell align="right">
                                                    {canManageThisGroup && (
                                                        <IconButton size="small" color="error" onClick={() => handleRemoveMember(m.id_employe)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </Card>
            </Stack>

            {/* Dialog création de groupe */}
            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Nouveau groupe</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} mt={1}>
                        {createError && <Typography color="error" variant="body2">{createError}</Typography>}
                        <TextField label="Nom" value={createForm.nom} onChange={e => setCreateForm(f => ({ ...f, nom: e.target.value }))} fullWidth autoFocus required />
                        <FormControl fullWidth>
                            <InputLabel>Groupe parent (optionnel)</InputLabel>
                            <Select
                                value={createForm.id_groupe_parent}
                                label="Groupe parent (optionnel)"
                                onChange={e => setCreateForm(f => ({ ...f, id_groupe_parent: e.target.value }))}
                            >
                                <MenuItem value="">— Aucun (racine) —</MenuItem>
                                {groups.map(g => (
                                    <MenuItem key={g.id_groupe} value={g.id_groupe}>{g.nom}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)} disabled={createSaving}>Annuler</Button>
                    <Button onClick={handleCreate} variant="contained" disabled={createSaving} sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                        {createSaving ? <CircularProgress size={20} color="inherit" /> : 'Créer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog confirmation suppression */}
            <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Supprimer le groupe</DialogTitle>
                <DialogContent>
                    {deleteError && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{deleteError}</Typography>}
                    <Typography>
                        Voulez-vous supprimer <strong>{deleteTarget?.nom}</strong> ?{' '}
                        {deleteTarget?.membres_count > 0 && `${deleteTarget.membres_count} membre(s) en seront retirés. `}
                        Cette action est irréversible (les sous-groupes doivent être supprimés/déplacés séparément).
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Annuler</Button>
                    <Button onClick={handleDelete} variant="contained" color="error" disabled={deleting}>
                        {deleting ? <CircularProgress size={20} color="inherit" /> : 'Supprimer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog ajout d'un membre */}
            <Dialog open={!!memberPicker} onClose={() => setMemberPicker(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Ajouter un membre</DialogTitle>
                <DialogContent>
                    {membersError && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{membersError}</Typography>}
                    <Autocomplete
                        options={candidateEmployes}
                        getOptionLabel={(o) => `${o.prenom} ${o.nom} (${o.email})`}
                        onChange={(e, value) => handleAddMember(value)}
                        loading={addingMember}
                        renderInput={(params) => <TextField {...params} label="Rechercher un employé" autoFocus sx={{ mt: 1 }} />}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMemberPicker(null)}>Fermer</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
